import { ActionIcon, Loader, Popover, Progress, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Archive, Bell, Download, Video } from "lucide-react";
import { useCallback, useEffect, useId, useRef, useState } from "react";

import { DownloadError, downloadSignedFile } from "../../api/download";
import type { EditRenderMaker, JobStatus } from "../../api/generated/model";
import { isSettled } from "../../platform/jobs/status";
import type { HereProgress, HereSave } from "./browser-save";
import { useRender } from "./project";
import classes from "./editor.module.css";

/** One export made in this browser, from the press of Download to the saved file. */
export interface LocalExport {
  id: string;
  startedAt: number;
  /** Frames drawn and encoded, of all of them. */
  done: number;
  frames: number;
  state: "exporting" | "saved" | "failed";
  problem: string | null;
  /** The made file, kept to save again from the bell. */
  file: Blob | null;
  /** The file's format, as its extension: "mp4", "webm", "wav". */
  extension: string;
}

/**
 * One render started in this editor: its job and when it started; a Download's (its file saved
 * to the computer) or a Save's (live's Save to project: kept in the Library, `name` its file, in
 * `folderId`, null when unfiled, which Try again saves into again).
 */
export interface ExportTask {
  jobId: string;
  startedAt: number;
  kind?: "download" | "save";
  name?: string;
  folderId?: string | null;
  /** A Save whose file this tab makes (spec fast-save), and one the server took over from it. */
  made?: EditRenderMaker;
  handedOver?: boolean;
}

/**
 * A Save made here in live's words (spec fast-save's layout): its frames are the bar's first
 * 90 %, its post the next 9, then "Finalizing..." while the server takes the file in.
 */
export function hereShown(here: HereProgress): { words: string; value: number } {
  if (here.phase === "sent") return { words: "Finalizing...", value: 99 };
  const value = here.phase === "making" ? here.done * 90 : 90 + here.done * 9;
  return { words: `Saving Video... ${Math.floor(value)}%`, value };
}

/** What a Save's row says while this tab makes its file, or why it failed; null otherwise. */
export function hereNow(
  here: HereSave | undefined,
  status: JobStatus | undefined,
): { words: string; value: number } | { problem: string } | null {
  if (here === undefined) return null;
  if (here.phase === "failed") return { problem: here.problem };
  // The server's render has the file once it takes it in or ends: its own words then.
  if (status === "finalizing" || (status !== undefined && isSettled(status))) return null;
  return hereShown(here);
}

/**
 * Where an export stands once its render ended: its file being saved to the computer, saved, or
 * failed (the render, with its own message, or the save, with `problem`).
 */
export type SaveState =
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; problem: string | null };

/** An export still running: no file saved and nothing failed yet. */
export function isActive(save: SaveState | undefined): boolean {
  return save === undefined || save.kind === "saving";
}

/** How far a render is, as a share of the bar, for the header line and the bell. */
export const STEP_VALUE: Record<JobStatus, number> = {
  queued: 10,
  submitting: 25,
  running: 55,
  finalizing: 85,
  succeeded: 100,
  failed: 100,
  expired: 100,
  canceled: 100,
};

/**
 * The bar's value: a running render's own progress once the Save worker says it (#269), spread
 * between the steps around it so the bar never goes back, else its step's.
 */
export function barValue(status: JobStatus | undefined, progress: number | null | undefined): number {
  if (status === undefined) return 2;
  if (status !== "running" || progress == null) return STEP_VALUE[status];
  return STEP_VALUE.submitting + progress * (STEP_VALUE.finalizing - STEP_VALUE.submitting);
}

/** A running render's words with its whole percent once it says one: live's "Saving Video... 42%". */
export function withPercent(
  words: string,
  status: JobStatus | undefined,
  progress: number | null | undefined,
): string {
  // Progress is kept in single precision: 0.42 comes back as 0.41999998, so a hair of slack.
  return status === "running" && progress != null ? `${words} ${Math.floor(progress * 100 + 1e-4)}%` : words;
}

/**
 * An export's name, live's bell's: `Download_YYYYMMDD_HHMM.<format>`, local time at its start.
 * The file is saved by it too, so what the bell lists is what lands in the downloads (live saves
 * `export-<epoch ms>.<format>`: the lead's call, the screen wins; E2 decision 22).
 */
export function bellName(startedAt: number, extension = "mp4"): string {
  const d = new Date(startedAt);
  const p = (n: number) => n.toString().padStart(2, "0");
  const day = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}`;
  return `Download_${day}_${p(d.getHours())}${p(d.getMinutes())}.${extension}`;
}

/** Why a finished export could not be saved, in words. */
function problemOf(error: unknown): string {
  const status = error instanceof DownloadError ? error.status : undefined;
  if (status === 403 || status === 404 || status === 410) {
    return "The file's link had expired; try again.";
  }
  if (status === undefined) return "The file could not be fetched here.";
  return `The file could not be fetched (status ${status}).`;
}

/**
 * The saves of this editor's exports, by render job: each finished export is saved to the
 * computer once, as live's editor does when its render ends (`start`), and again on request
 * (`retry`).
 */
export function useExportSaves() {
  const [saves, setSaves] = useState<Record<string, SaveState>>({});
  const begun = useRef(new Set<string>());
  const save = useCallback(async (jobId: string, url: string, name: string) => {
    setSaves((current) => ({ ...current, [jobId]: { kind: "saving" } }));
    try {
      await downloadSignedFile(url, name);
      setSaves((current) => ({ ...current, [jobId]: { kind: "saved" } }));
    } catch (error) {
      setSaves((current) => ({
        ...current,
        [jobId]: { kind: "failed", problem: problemOf(error) },
      }));
    }
  }, []);
  /** A render that succeeded: its file is saved once. */
  const start = useCallback(
    (jobId: string, url: string, name: string) => {
      if (begun.current.has(jobId)) return;
      begun.current.add(jobId);
      void save(jobId, url, name);
    },
    [save],
  );
  /** A render that failed: nothing to save. */
  const fail = useCallback((jobId: string) => {
    if (begun.current.has(jobId)) return;
    begun.current.add(jobId);
    setSaves((current) => ({ ...current, [jobId]: { kind: "failed", problem: null } }));
  }, []);
  const retry = useCallback(
    (jobId: string, url: string, name: string) => void save(jobId, url, name),
    [save],
  );
  /** A Save's render that succeeded: the video is in the Library, nothing to save here. */
  const kept = useCallback((jobId: string) => {
    if (begun.current.has(jobId)) return;
    begun.current.add(jobId);
    setSaves((current) => ({ ...current, [jobId]: { kind: "saved" } }));
  }, []);
  return { saves, start, fail, retry, kept };
}

interface TaskProps {
  teamId: string;
  brandId: string;
  projectId: string;
  task: ExportTask;
}

/**
 * Saves an export to the computer once its render succeeds, or marks it failed when the render
 * does not. It draws nothing.
 */
export function ExportSaver(
  props: TaskProps & {
    onDone: (jobId: string, url: string, name: string) => void;
    onFailed: (jobId: string) => void;
  },
) {
  const { teamId, brandId, projectId, task, onDone, onFailed } = props;
  const render = useRender(teamId, brandId, projectId, task.jobId);
  const status = render.data?.status;
  const url = status === "succeeded" ? render.data?.url ?? null : null;
  const failed = status !== undefined && isSettled(status) && status !== "succeeded";
  useEffect(() => {
    if (url !== null) onDone(task.jobId, url, bellName(task.startedAt));
    else if (failed) onFailed(task.jobId);
  }, [url, failed, task.jobId, task.startedAt, onDone, onFailed]);
  return null;
}

/** The status word of an export in the bell, live's words (a Save's while it renders, "Saved"). */
function statusOf(status: JobStatus | undefined, save: SaveState | undefined, kept: boolean): string {
  if (status === undefined || status === "queued" || status === "submitting") return "Starting...";
  if (kept) {
    if (status === "running") return "Saving Video...";
    if (status === "finalizing") return "Finalizing...";
    return status === "succeeded" ? "Saved" : "Failed";
  }
  if (status === "running") return "Exporting...";
  if (status === "finalizing") return "Finalizing...";
  if (status !== "succeeded") return "Failed";
  if (save?.kind === "failed") return "Failed";
  return save?.kind === "saved" ? "Downloaded" : "Finalizing...";
}

function BellRow(
  props: TaskProps & {
    save: SaveState | undefined;
    /** Where this tab stands on the Save's file, when it makes it (spec fast-save). */
    here: HereSave | undefined;
    onSaveAgain: (jobId: string, url: string, name: string) => void;
    /** A failed Save's Try again, and whether it may run now (no save or render starting). */
    onTryAgain: () => void;
    canTryAgain: boolean;
  },
) {
  const { teamId, brandId, projectId, task, save, here, onSaveAgain, onTryAgain, canTryAgain } = props;
  const render = useRender(teamId, brandId, projectId, task.jobId);
  const status = render.data?.status;
  const progress = render.data?.progress;
  const kept = task.kind === "save";
  const now = hereNow(here, status);
  const handFailed = now !== null && "problem" in now;
  const word = handFailed ? "Failed" : now !== null ? now.words : statusOf(status, save, kept);
  const name = kept ? (task.name ?? bellName(task.startedAt)) : bellName(task.startedAt);
  const working = word.endsWith("...") || word.endsWith("%");
  const renderFailed = handFailed || (status !== undefined && isSettled(status) && status !== "succeeded");
  const problem = handFailed
    ? now.problem
    : save?.kind === "failed" && save.problem !== null
      ? save.problem
      : renderFailed
        ? (render.data?.error?.message ?? "The export failed.")
        : null;
  const said = now !== null && !handFailed ? word : withPercent(word, status, progress);
  const started = startedText(task.startedAt);
  return (
    <li className={classes.bellRow}>
      <span className={classes.bellThumb} aria-hidden>
        {working ? <Loader size={18} /> : <Video size={18} />}
      </span>
      <div className={classes.bellText}>
        <Text className={classes.bellName}>{name}</Text>
        <Text className={classes.bellMeta}>
          <b>{kept ? "Save" : "Download"}</b> · {said}
          {task.handedOver === true ? " · on the server" : ""}
        </Text>
        <Text className={classes.bellMeta}>{started}</Text>
        {working ? (
          <Progress
            className={classes.bellProgress}
            size={4}
            value={now !== null && !handFailed ? now.value : barValue(status, progress)}
            aria-label={`${name}: ${word}`}
          />
        ) : null}
        {problem === null ? null : <Text className={classes.bellProblem}>{problem}</Text>}
        {/* A failed Save says why, and saves again into its folder (spec save-failures). */}
        {kept && renderFailed ? (
          <UnstyledButton
            className={classes.bellRetry}
            aria-label={`Try saving ${name} again`}
            disabled={!canTryAgain}
            onClick={onTryAgain}
          >
            Try again
          </UnstyledButton>
        ) : null}
      </div>
      {/* Live's row offers the file once it exists; ours saves it again. */}
      {!kept && status === "succeeded" && render.data?.url != null && !working ? (
        <Tooltip label="Download again">
          <ActionIcon
            variant="subtle"
            color="gray"
            size={28}
            aria-label={`Download ${bellName(task.startedAt)} again`}
            onClick={() => {
              const url = render.data?.url;
              if (url != null) onSaveAgain(task.jobId, url, bellName(task.startedAt));
            }}
          >
            <Download size={14} />
          </ActionIcon>
        </Tooltip>
      ) : null}
    </li>
  );
}

/** When an export started, as live's bell writes it: "Oct 16 2026 10:42:07". */
function startedText(startedAt: number): string {
  return new Date(startedAt)
    .toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(",", "");
}

/** The status word of an export made in this browser, live's words. */
const LOCAL_WORD: Record<LocalExport["state"], string> = {
  exporting: "Exporting...",
  saved: "Downloaded",
  failed: "Failed",
};

/** A bell row for an export made in this browser: its frames as the bar, then its file. */
function LocalBellRow(props: {
  task: LocalExport;
  onSaveAgain: (file: Blob, name: string) => void;
}) {
  const { task, onSaveAgain } = props;
  const word = LOCAL_WORD[task.state];
  const working = task.state === "exporting";
  const name = bellName(task.startedAt, task.extension);
  return (
    <li className={classes.bellRow}>
      <span className={classes.bellThumb} aria-hidden>
        {working ? <Loader size={18} /> : <Video size={18} />}
      </span>
      <div className={classes.bellText}>
        <Text className={classes.bellName}>{name}</Text>
        <Text className={classes.bellMeta}>
          <b>Download</b> · {word}
        </Text>
        <Text className={classes.bellMeta}>{startedText(task.startedAt)}</Text>
        {working ? (
          <Progress
            className={classes.bellProgress}
            size={4}
            value={task.frames === 0 ? 0 : (task.done / task.frames) * 100}
            aria-label={`${name}: ${word}`}
          />
        ) : null}
        {task.problem === null ? null : <Text className={classes.bellProblem}>{task.problem}</Text>}
      </div>
      {task.file === null ? null : (
        <Tooltip label="Download again">
          <ActionIcon
            variant="subtle"
            color="gray"
            size={28}
            aria-label={`Download ${name} again`}
            onClick={() => {
              if (task.file !== null) onSaveAgain(task.file, bellName(task.startedAt, task.extension));
            }}
          >
            <Download size={14} />
          </ActionIcon>
        </Tooltip>
      )}
    </li>
  );
}

/** What the bell lists: this editor's exports and saves, and what to do with them. */
export type BellProps = Omit<TaskProps, "task"> & {
  tasks: readonly ExportTask[];
  local: readonly LocalExport[];
  saves: Record<string, SaveState>;
  /** The Saves this tab makes the file of, by their render's job (spec fast-save). */
  here: Readonly<Record<string, HereSave>>;
  onClear: () => void;
  onSaveAgain: (jobId: string, url: string, name: string) => void;
  onSaveLocalAgain: (file: Blob, name: string) => void;
  /** Saves a failed Save's project again, into its folder: a new render, a new key. */
  onTryAgain: (task: ExportTask) => void;
  canTryAgain: boolean;
};

/** The bell's counts: the finished rows by key, the ones not yet read, and whether one runs. */
export function bellState(props: Pick<BellProps, "tasks" | "local" | "saves">, read: ReadonlySet<string>) {
  const finishedKeys = [
    ...props.tasks.filter((task) => !isActive(props.saves[task.jobId])).map((task) => task.jobId),
    ...props.local.filter((task) => task.state !== "exporting").map((task) => task.id),
  ];
  const active =
    props.tasks.some((task) => isActive(props.saves[task.jobId])) ||
    props.local.some((task) => task.state === "exporting");
  return { finishedKeys, unseen: finishedKeys.filter((key) => !read.has(key)).length, active };
}

/**
 * The bell's list, live's: "Mark all as read", which reads the finished rows, "Clear", which
 * takes them away, and every export and save newest first, each with its status and, while it
 * runs, a progress bar. The desktop's bell shows it in its popover, a phone in its Notifications
 * sheet (spec phone-layout.md), which names it itself (`titleId` null).
 */
export function ExportList(
  props: BellProps & { read: ReadonlySet<string>; onRead: (read: ReadonlySet<string>) => void; titleId: string | null },
) {
  const { tasks, local, saves, here, onClear, onSaveAgain, onSaveLocalAgain, onTryAgain, canTryAgain, read, onRead, titleId, ...where } =
    props;
  const { finishedKeys, unseen } = bellState(props, read);
  const clear = () => {
    onClear();
    onRead(new Set());
  };
  // Newest first, whichever made them.
  const rows = [
    ...tasks.map((task) => ({ at: task.startedAt, key: task.jobId, server: task, mine: null })),
    ...local.map((task) => ({ at: task.startedAt, key: task.id, server: null, mine: task })),
  ].sort((one, other) => other.at - one.at);
  return (
    <>
      <div className={classes.bellHeader}>
        {titleId === null ? null : (
          <Text className={classes.bellTitle} id={titleId}>
            Notifications
          </Text>
        )}
        <div className={classes.bellActions}>
          {unseen > 0 ? (
            <UnstyledButton className={classes.bellClear} onClick={() => onRead(new Set(finishedKeys))}>
              Mark all as read
            </UnstyledButton>
          ) : null}
          {finishedKeys.length > 0 ? (
            <UnstyledButton className={classes.bellClear} onClick={clear}>
              Clear
            </UnstyledButton>
          ) : null}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className={classes.bellEmpty}>
          <Archive size={20} aria-hidden />
          <Text size="xs">No recent notifications</Text>
        </div>
      ) : (
        <ul className={classes.bellList}>
          {rows.map((row) =>
            row.mine !== null ? (
              <LocalBellRow key={row.key} task={row.mine} onSaveAgain={onSaveLocalAgain} />
            ) : row.server !== null ? (
              <BellRow
                key={row.key}
                {...where}
                task={row.server}
                save={saves[row.server.jobId]}
                here={here[row.server.jobId]}
                onSaveAgain={onSaveAgain}
                onTryAgain={() => onTryAgain(row.server)}
                canTryAgain={canTryAgain}
              />
            ) : null,
          )}
        </ul>
      )}
    </>
  );
}

/**
 * Live's bell: `ExportList` in a popover from the header's bell. It spins while one runs and
 * counts the finished ones not yet read, as live's does.
 */
export function ExportBell(props: BellProps) {
  const [opened, setOpened] = useState(false);
  // The finished rows read, by key (live keeps a read mark on each).
  const [read, setRead] = useState<ReadonlySet<string>>(new Set());
  const title = useId();
  const { unseen, active } = bellState(props, read);
  const toggle = () => setOpened(!opened);
  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-end" width={320} shadow="md">
      <Popover.Target>
        <Tooltip label="Notifications" disabled={opened}>
          <ActionIcon
            variant="subtle"
            color="gray"
            size={36}
            radius="xl"
            className={classes.bell}
            aria-label={unseen > 0 ? `Notifications, ${unseen} new` : "Notifications"}
            // The tooltip between them keeps Mantine's popup state off the button: set here.
            aria-haspopup="dialog"
            aria-expanded={opened}
            data-open={opened || undefined}
            onClick={toggle}
            // Escape closes it from the bell too, as live's does; the dropdown handles its own.
            onKeyDown={(event) => {
              if (event.key === "Escape" && opened) setOpened(false);
            }}
          >
            {active ? <Loader size={16} /> : <Bell size={20} />}
            {unseen > 0 ? (
              <span className={classes.bellCount} aria-hidden>
                {unseen}
              </span>
            ) : null}
          </ActionIcon>
        </Tooltip>
      </Popover.Target>
      {/* Mantine gives the dropdown its dialog role; its own title names it. */}
      <Popover.Dropdown className={classes.bellPanel} aria-labelledby={title}>
        <ExportList {...props} read={read} onRead={setRead} titleId={title} />
      </Popover.Dropdown>
    </Popover>
  );
}
